import { memo, useMemo } from "react";
import { rgbToCss } from "utils/rgbToCss";
import { ROUTE_STROKE_WIDTH } from "../CircuitPathsLayer/constants";
import { FinalizedRouteRender } from "./FinalizedRouteRender";
import { ActiveRouteRender } from "./ActiveRouteRender";
import { CREATION_RGB, HOVER_LINE_DASH_ARRAY } from "./constants";
import type { RoutesCreationLayerProps } from "./types";

export const RoutesCreationLayer = memo(function RoutesCreationLayer({
  overlay: {
    routes,
    hoverPosition,
    isEditable = false,
    selectedNodeId = null,
    onNodeMouseDown,
    onSegmentMouseDown,
  },
  zoomLevel,
  circleSizeScale = 1,
}: RoutesCreationLayerProps) {
  const finalizedRoutes = useMemo(
    () => routes.filter((route) => !route.isActive),
    [routes],
  );

  const activeRoute = useMemo(
    () => routes.find((route) => route.isActive) ?? null,
    [routes],
  );

  const lastActiveNode =
    activeRoute && activeRoute.nodes.length > 0
      ? activeRoute.nodes[activeRoute.nodes.length - 1]
      : null;

  const showHoverLine = activeRoute && lastActiveNode && hoverPosition !== null;

  return (
    <g>
      {finalizedRoutes.map((route) => (
        <FinalizedRouteRender
          key={route.id}
          route={route}
          zoomLevel={zoomLevel}
          circleSizeScale={circleSizeScale}
        />
      ))}
      {showHoverLine && lastActiveNode && hoverPosition && activeRoute && (
        <line
          x1={lastActiveNode.x}
          y1={lastActiveNode.y}
          x2={hoverPosition.x}
          y2={hoverPosition.y}
          stroke={rgbToCss(CREATION_RGB)}
          strokeWidth={ROUTE_STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={HOVER_LINE_DASH_ARRAY}
          vectorEffect="non-scaling-stroke"
          opacity={0.6}
        />
      )}
      {activeRoute && (
        <ActiveRouteRender
          route={activeRoute}
          isEditable={isEditable}
          selectedNodeId={selectedNodeId}
          zoomLevel={zoomLevel}
          circleSizeScale={circleSizeScale}
          onNodeMouseDown={onNodeMouseDown}
          onSegmentMouseDown={onSegmentMouseDown}
        />
      )}
    </g>
  );
});

export type { RoutesCreationLayerProps };
